import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { FuelTankModel } from '../client/ders'
import { useGetFuelTanksQuery } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { FuelTankCacheRecord } from './storage/schema'
import { useGateValue } from '~/configs/statsig'

const FUEL_TANKS_PAGE_SIZE = 1000

// Stable reference for the gated-off path (the common case for most orgs), so a
// fresh [] doesn't bust the manager's useMemo and re-render every accessor.
const EMPTY_FUEL_TANKS: FuelTankModel[] = []

export type FuelTanksDataManagerContextValue = {
  data: FuelTankModel[]
  isLoading: boolean
  isFetching: boolean
}

function useFuelTanksQuery(enabled: boolean, options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetFuelTanksQuery(
    {
      orgIds: principalOrgId ? [principalOrgId] : undefined,
      orgDepth: 100,
      includeLocalManaged: true,
      page: 0,
      count: FUEL_TANKS_PAGE_SIZE
    },
    combineSkip(!principalOrgId || !enabled, options)
  )

  return {
    fuelTanks: data?.data,
    isLoading,
    isFetching
  }
}

export default function useFuelTanksDataManager(service: IndexedDbService) {
  // Fuel tanks are behind the fuel-tanks-in-ui gate. Like solar arrays, this
  // both skips the fetch AND withholds any hydrated cache for orgs without the
  // feature, so a cache populated in a prior gated-on session can't leak tanks
  // into surfaces that no longer check the gate while detail pages still 404.
  const hasFuelTanksInUi = useGateValue('fuel-tanks-in-ui')
  const { fuelTanks, isLoading, isFetching } = useFuelTanksQuery(hasFuelTanksInUi)

  const data = useFuelTanksPersistence(service, fuelTanks, hasFuelTanksInUi)

  return useMemo<FuelTanksDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching
    }),
    [data, isLoading, isFetching]
  )
}

function useFuelTanksPersistence(
  service: IndexedDbService,
  fuelTanks: FuelTankModel[] | undefined,
  enabled: boolean
): FuelTankModel[] {
  const [hydratedFuelTanks, setHydratedFuelTanks] = useState<FuelTankModel[]>([])

  useEffect(() => {
    // Don't surface a previously-cached set when the feature is gated off.
    if (!enabled) {
      setHydratedFuelTanks([])
      return
    }

    async function hydrateFuelTanksCache() {
      try {
        const records = await service.getAll('fuelTanks')
        setHydratedFuelTanks(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate fuel tanks cache', error)
      }
    }

    void hydrateFuelTanksCache()
  }, [service, enabled])

  useEffect(() => {
    if (!fuelTanks) return

    const fetchedAt = Date.now()
    const records: FuelTankCacheRecord[] = fuelTanks.map((fuelTank) => ({
      id: fuelTank.id,
      payload: fuelTank,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('fuelTanks', records)
      } catch (error) {
        logger.error('Failed to persist fuel tanks snapshot', error)
      }
    }

    void replaceRecords()
  }, [fuelTanks, service])

  return enabled ? (fuelTanks ?? hydratedFuelTanks) : EMPTY_FUEL_TANKS
}
